import { 
  RideSchema, 
  FleetSettings, 
  SyntheticRideData,
  SyntheticFleetData,
  SyntheticTimeWindowData,
  FleetPresetType
} from '../../types/simulation';

// London coordinates for proper testing
const LONDON_CENTER_LAT = 51.5074;
const LONDON_CENTER_LNG = -0.1278;

// Synthetic ride data for testing - London area
export const syntheticRideData: SyntheticRideData = {
  name: 'test-rides-001',
  rides: [
    {
      external_reference_id: 'ride-001',
      external_user_reference_id: 'user-001',
      from_latitude: LONDON_CENTER_LAT + 0.001,
      from_longitude: LONDON_CENTER_LNG + 0.001,
      to_latitude: LONDON_CENTER_LAT - 0.001,
      to_longitude: LONDON_CENTER_LNG - 0.001,
      from_address: 'London Bridge Station',
      to_address: 'Tower Bridge',
      request_type: 'departure_fixed',
      passengers: [
        { number_of_passengers: 1, type: 'standard' }
      ],
      request_time: '2024-01-15T08:00:00Z'
    },
    {
      external_reference_id: 'ride-002',
      external_user_reference_id: 'user-002',
      from_latitude: LONDON_CENTER_LAT - 0.001,
      from_longitude: LONDON_CENTER_LNG - 0.001,
      to_latitude: LONDON_CENTER_LAT + 0.001,
      to_longitude: LONDON_CENTER_LNG + 0.001,
      from_address: 'Tower Bridge',
      to_address: 'London Bridge Station',
      request_type: 'arrival_fixed',
      passengers: [
        { number_of_passengers: 1, type: 'standard' }
      ],
      request_time: '2024-01-15T17:00:00Z'
    },
    {
      external_reference_id: 'ride-003',
      external_user_reference_id: 'user-003',
      from_latitude: LONDON_CENTER_LAT + 0.002,
      from_longitude: LONDON_CENTER_LNG + 0.002,
      to_latitude: LONDON_CENTER_LAT - 0.002,
      to_longitude: LONDON_CENTER_LNG - 0.002,
      from_address: 'Borough Market',
      to_address: 'Southwark Cathedral',
      request_type: 'departure_fixed',
      passengers: [
        { number_of_passengers: 1, type: 'wheelchair' }
      ],
      request_time: '2024-01-15T09:00:00Z'
    }
  ]
};

// Synthetic fleet data for testing - London area
export const syntheticFleetData: SyntheticFleetData = {
  name: 'test-fleet-001',
  fleet: {
    vehicles: [
      {
        number_of_vehicles: 1,
        vehicle: {
          capacity: [
            { type: "standard", number: 4 },
            { type: "wheelchair", number: 0 }
          ],
          brand: "test_brand",
          vehicle_type: "standard",
          shifts: [
            {
              start_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
              end_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
              start_time: "2024-01-15T06:00:00.000",
              end_time: "2024-01-15T22:00:00.000"
            }
          ]
        }
      },
      {
        number_of_vehicles: 1,
        vehicle: {
          capacity: [
            { type: "standard", number: 2 },
            { type: "wheelchair", number: 1 }
          ],
          brand: "test_brand",
          vehicle_type: "wheelchair",
          shifts: [
            {
              start_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
              end_address: { latitude: LONDON_CENTER_LAT, longitude: LONDON_CENTER_LNG },
              start_time: "2024-01-15T06:00:00.000",
              end_time: "2024-01-15T22:00:00.000"
            }
          ]
        }
      }
    ]
  }
};

// Synthetic time window data for testing
export const syntheticTimeWindowData: SyntheticTimeWindowData = {
  name: 'test-time-window-001',
  timeWindow: {
    time_model_type: 'slope_and_intercept',
    settings: {
      slope: 2.0,
      intercept: 1200,
      window: 1200,
      max_time_minutes: 90,
      once_off_processing_time_seconds: 15,
      processing_time: [
        {
          seat_type: 'standard',
          processing_time_seconds: 30
        },
        {
          seat_type: 'wheelchair',
          processing_time_seconds: 90
        }
      ]
    },
    ride_settings: {
      rides_name: 'test-rides-001',
      batch_id: null
    }
  }
};

// Synthetic problem config for testing
export const syntheticProblemConfig = {
  id: 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  }),
  configuration: {
    iterationCount: 1500,
    travelTimeMultiplier: 0.98,
    detailed_path_required: true
  }
};

// Complete synthetic scenario for testing
export const syntheticScenarioConfig = {
  scenario_name: 'test-scenario-001',
  rides: syntheticRideData.rides,
  time_window_settings: syntheticTimeWindowData.timeWindow,
  fleet: syntheticFleetData.fleet,
  problem_config: syntheticProblemConfig
};

// Helper function to generate unique synthetic data
export function generateSyntheticRides(count: number = 5): RideSchema[] {
  const rides: RideSchema[] = [];
  const baseLat = LONDON_CENTER_LAT;
  const baseLng = LONDON_CENTER_LNG;
  
  // Generate proper UUIDs
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };
  
  // Generate rides during fleet active hours (6 AM to 10 PM)
  const fleetStartHour = 6;
  const fleetEndHour = 22;
  
  for (let i = 0; i < count; i++) {
    // Generate time within fleet active hours
    const hour = fleetStartHour + Math.random() * (fleetEndHour - fleetStartHour);
    const minute = Math.floor(Math.random() * 60);
    
    // Create timezone-agnostic datetime string (no UTC conversion)
    const year = 2024;
    const month = 1; // January
    const day = 15;
    const hours = Math.floor(hour);
    const minutes = minute;
    const seconds = 0;
    const requestTime = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.000`;
    
    rides.push({
      external_reference_id: `synthetic-ride-${i + 1}`,
      external_user_reference_id: `synthetic-user-${i + 1}`,
      from_latitude: baseLat + (Math.random() - 0.5) * 0.01, // Within ~1km of center
      from_longitude: baseLng + (Math.random() - 0.5) * 0.01,
      to_latitude: baseLat + (Math.random() - 0.5) * 0.01,
      to_longitude: baseLng + (Math.random() - 0.5) * 0.01,
      request_type: Math.random() > 0.5 ? 'departure_fixed' : 'arrival_fixed',
      passengers: [
        { 
          number_of_passengers: Math.floor(Math.random() * 3) + 1, // 1-3 passengers
          type: Math.random() > 0.1 ? 'standard' : 'wheelchair'
        }
      ],
      request_time: requestTime,
      ride_id: generateUUID(),
      batch_id: generateUUID()
    });
  }
  
  return rides;
}

export function generateIntelligentFleet(rides: RideSchema[]): FleetSettings {
  // Analyze ride demand patterns
  const analysis = analyzeTripsPerHour(rides);
  const maxTripsPerHour = analysis.maxTripsPerHour;
  
  // Calculate base vehicle count: max trips per hour divided by 3 (conservative estimate)
  const baseVehicleCount = Math.max(1, Math.ceil(maxTripsPerHour / 3));
  
  // Get the date from rides to match vehicle schedules
  const rideDate = rides.length > 0 && rides[0]?.request_time 
    ? rides[0].request_time.split('T')[0] 
    : '2024-01-15';
  
  // Calculate centroid of ride locations for optimal positioning
  let centroidLat = 51.5074; // Default to London
  let centroidLng = -0.1278;
  
  if (rides.length > 0) {
    let totalLat = 0;
    let totalLng = 0;
    let count = 0;
    
    rides.forEach(ride => {
      totalLat += ride.from_latitude + ride.to_latitude;
      totalLng += ride.from_longitude + ride.to_longitude;
      count += 2;
    });
    
    centroidLat = totalLat / count;
    centroidLng = totalLng / count;
  }
  
  return {
    vehicles: [{
      vehicle: {
        brand: "Generic",
        vehicle_type: "Bus",
        external_reference_id: "intelligent-vehicle-1",
        capacity: [
          { type: "standard", number: 8 },
          { type: "wheelchair", number: 1 }
        ],
        shifts: [
          {
            start_time: `${rideDate} 06:00:00`,
            end_time: `${rideDate} 22:00:00`,
            start_address: {
              latitude: centroidLat,
              longitude: centroidLng
            },
            end_address: {
              latitude: centroidLat,
              longitude: centroidLng
            }
          }
        ]
      },
      number_of_vehicles: baseVehicleCount
    }]
  };
}

export function generateSyntheticFleet(vehicleCount: number = 3, rides?: RideSchema[]): FleetSettings {
  const vehicles = [];
  const baseLat = -36.8485;
  const baseLng = 174.7633;
  
  // Get the date from rides if available, otherwise use default
  const rideDate = rides && rides.length > 0 && rides[0]?.request_time 
    ? rides[0].request_time.split('T')[0] 
    : '2024-01-15';
  
  for (let i = 0; i < vehicleCount; i++) {
    const isWheelchair = Math.random() > 0.8;
    const standardCapacity = Math.random() > 0.5 ? 4 : 2;
    const wheelchairCapacity = isWheelchair ? 1 : 0;
    
    vehicles.push({
      number_of_vehicles: 1,
      vehicle: {
        capacity: [
          { type: "standard", number: standardCapacity },
          { type: "wheelchair", number: wheelchairCapacity }
        ],
        brand: "synthetic_brand",
        vehicle_type: isWheelchair ? "wheelchair" : "standard",
        shifts: [
          {
            start_address: {
              latitude: baseLat + (Math.random() - 0.5) * 0.01,
              longitude: baseLng + (Math.random() - 0.5) * 0.01
            },
            end_address: {
              latitude: baseLat + (Math.random() - 0.5) * 0.01,
              longitude: baseLng + (Math.random() - 0.5) * 0.01
            },
            start_time: `${rideDate} 06:00:00`,
            end_time: `${rideDate} 22:00:00`
          }
        ]
      }
    });
  }
  
  return {
    vehicles
  };
}

// Fleet preset type definition
interface FleetPreset {
  type: FleetPresetType;
  name: string;
  description: string;
  vehicleAdjustment: number;
}

// Fleet presets configuration
export const FLEET_PRESETS: FleetPreset[] = [
  {
    type: 'auto',
    name: 'Auto',
    description: 'Automatically calculated based on ride demand',
    vehicleAdjustment: 0
  },
  {
    type: 'savvy',
    name: 'Savvy',
    description: 'Cost-conscious fleet (1 less vehicle)',
    vehicleAdjustment: -1
  },
  {
    type: 'platinum',
    name: 'Platinum',
    description: 'Premium service (1 extra vehicle)',
    vehicleAdjustment: 1
  }
];

// Apply fleet preset to an existing fleet
export function applyFleetPreset(baseFleet: FleetSettings, presetType: FleetPresetType): FleetSettings {
  const preset = FLEET_PRESETS.find(p => p.type === presetType);
  if (!preset || preset.vehicleAdjustment === 0) {
    return baseFleet; // Return original fleet for 'auto' or if preset not found
  }

  // Calculate the base vehicle count from the intelligent fleet
  const baseTotal = baseFleet.vehicles.reduce((sum, v) => sum + v.number_of_vehicles, 0);
  
  // Calculate target total: base + adjustment, minimum 1
  const targetTotal = Math.max(1, baseTotal + preset.vehicleAdjustment);
  
  // Create a simple fleet with the target vehicle count
  // Use the first vehicle as the template
  if (baseFleet.vehicles.length > 0) {
    const templateVehicle = baseFleet.vehicles[0];
    
    return {
      ...baseFleet,
      vehicles: [{
        ...templateVehicle,
        number_of_vehicles: targetTotal
      }]
    };
  }

  return baseFleet;
}

// Generate fleet with preset applied
export function generateFleetWithPreset(rides: RideSchema[], presetType: FleetPresetType): FleetSettings {
  const baseFleet = generateIntelligentFleet(rides);
  return applyFleetPreset(baseFleet, presetType);
}

// Analyze trips per hour for fleet optimization
export function analyzeTripsPerHour(rides: RideSchema[]): { maxTripsPerHour: number; hourlyDistribution: { [hour: string]: number } } {
  const tripsByHour: { [hour: number]: number } = {};
  
  rides.forEach(ride => {
    const hour = new Date(ride.request_time || ride.created_at || new Date()).getHours();
    tripsByHour[hour] = (tripsByHour[hour] || 0) + 1;
  });
  
  const maxTripsPerHour = Math.max(...Object.values(tripsByHour), 0);
  const hourlyDistribution = Object.fromEntries(
    Object.entries(tripsByHour).map(([hour, count]) => [hour.toString(), count])
  );
  
  return {
    maxTripsPerHour,
    hourlyDistribution
  };
} 